import { zodResolver } from "@hookform/resolvers/zod";
import { Link } from "@tanstack/react-router";
import { ArrowRight, Loader2, Lock, Mail } from "lucide-react";
import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { BrandButton } from "@/components/brand/brand-button";
import { BrandInput } from "@/components/brand/brand-input";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "@/components/ui/form";
import { useSession } from "@/hooks/auth-hooks";
import { signIn, signUp } from "@/lib/auth-client";
import { useWebHaptics } from "@/lib/haptics";
import { emailSchema, passwordSchema } from "@/lib/validation";

const signupSchema = z.object({
	email: emailSchema,
	password: passwordSchema,
	subscribeToRadar: z.boolean(),
});

type SignupFormValues = z.infer<typeof signupSchema>;

function getPasswordStrength(password: string): {
	score: number;
	label: string;
	color: string;
} {
	let score = 0;
	if (password.length >= 8) score++;
	if (password.length >= 12) score++;
	if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++;
	if (/\d/.test(password)) score++;
	if (/[^a-zA-Z0-9]/.test(password)) score++;

	if (score <= 1)
		return { score: 1, label: "Weak", color: "bg-[color:var(--ticker-down)]" };
	if (score <= 2) return { score: 2, label: "Fair", color: "bg-amber-500" };
	if (score <= 3) return { score: 3, label: "Medium", color: "bg-amber-400" };
	if (score <= 4) return { score: 4, label: "Strong", color: "bg-brand" };
	return { score: 5, label: "Very strong", color: "bg-brand" };
}

interface SignupFormProps {
	/**
	 * Where social OAuth returns to, and where the email/password flow navigates
	 * after sign-up when {@link SignupFormProps.onSignedIn} is not provided.
	 */
	postAuthUrl: string;
	/**
	 * When provided, the email/password flow stays in place and calls this after
	 * the session is established instead of navigating. Used for the in-dialog
	 * sign-up where we proceed to the next step without leaving the page.
	 */
	onSignedIn?: () => void;
	autoFocus?: boolean;
}

export function SignupForm({
	postAuthUrl,
	onSignedIn,
	autoFocus,
}: SignupFormProps) {
	const { trigger } = useWebHaptics();
	const { refetch } = useSession();
	const [googleLoading, setGoogleLoading] = useState(false);
	const [githubLoading, setGithubLoading] = useState(false);

	const form = useForm<SignupFormValues>({
		resolver: zodResolver(signupSchema),
		mode: "onSubmit",
		reValidateMode: "onChange",
		defaultValues: { email: "", password: "", subscribeToRadar: true },
	});

	const password = form.watch("password");
	const passwordStrength = useMemo(
		() => getPasswordStrength(password ?? ""),
		[password],
	);

	const onSubmit = async (values: SignupFormValues) => {
		try {
			const result = await signUp.email({
				email: values.email,
				password: values.password,
				name: values.email.split("@")[0],
				callbackURL: postAuthUrl,
			});

			if (result.error) {
				form.setError("root.serverError", {
					message: result.error.message || "Failed to sign up",
				});
				return;
			}
			if (!values.subscribeToRadar) {
				const unsubscribeResponse = await fetch(
					"/api/newsletter/subscription",
					{
						method: "DELETE",
					},
				);
				if (!unsubscribeResponse.ok) {
					throw new Error("Account created, but failed to update Radar emails");
				}
			}
			trigger("success");
			await refetch();
			if (onSignedIn) {
				onSignedIn();
				return;
			}
			window.location.assign(postAuthUrl);
		} catch (err) {
			form.setError("root.serverError", {
				message: err instanceof Error ? err.message : "An error occurred",
			});
		}
	};

	const handleGoogleSignIn = async () => {
		form.clearErrors("root.serverError");
		setGoogleLoading(true);
		trigger("success");
		try {
			await signIn.social({ provider: "google", callbackURL: postAuthUrl });
		} catch (err) {
			form.setError("root.serverError", {
				message:
					err instanceof Error ? err.message : "Failed to sign in with Google",
			});
			setGoogleLoading(false);
		}
	};

	const handleGithubSignIn = async () => {
		form.clearErrors("root.serverError");
		setGithubLoading(true);
		trigger("success");
		try {
			await signIn.social({ provider: "github", callbackURL: postAuthUrl });
		} catch (err) {
			form.setError("root.serverError", {
				message:
					err instanceof Error ? err.message : "Failed to sign in with GitHub",
			});
			setGithubLoading(false);
		}
	};

	const loading = form.formState.isSubmitting;
	const serverError = form.formState.errors.root?.serverError?.message;

	return (
		<div>
			{serverError && (
				<div className="mb-4 border border-destructive/30 bg-destructive/10 px-3 py-2 font-mono text-sm uppercase tracking-[0.12em] text-destructive">
					{serverError}
				</div>
			)}

			<div className="space-y-3">
				<BrandButton
					type="button"
					variant="outline"
					size="lg"
					onClick={handleGoogleSignIn}
					disabled={googleLoading}
					className="w-full"
				>
					{googleLoading ? (
						<Loader2 className="size-4 animate-spin" />
					) : (
						<svg
							className="size-4"
							viewBox="0 0 24 24"
							aria-hidden="true"
							focusable="false"
						>
							<path
								fill="#4285F4"
								d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
							/>
							<path
								fill="#34A853"
								d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
							/>
							<path
								fill="#FBBC05"
								d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
							/>
							<path
								fill="#EA4335"
								d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
							/>
						</svg>
					)}
					{googleLoading ? "Signing in" : "Continue with Google"}
				</BrandButton>

				<BrandButton
					type="button"
					variant="outline"
					size="lg"
					onClick={handleGithubSignIn}
					disabled={githubLoading}
					className="w-full"
				>
					{githubLoading ? (
						<Loader2 className="size-4 animate-spin" />
					) : (
						<svg
							className="size-4"
							viewBox="0 0 24 24"
							aria-hidden="true"
							focusable="false"
							fill="currentColor"
						>
							<path d="M12 0C5.37 0 0 5.37 0 12c0 5.3 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61-.546-1.385-1.335-1.755-1.335-1.755-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 21.795 24 17.295 24 12c0-6.63-5.37-12-12-12z" />
						</svg>
					)}
					{githubLoading ? "Signing in" : "Continue with GitHub"}
				</BrandButton>

				<div className="relative py-1">
					<div className="absolute inset-0 flex items-center">
						<div className="h-px w-full bg-border" />
					</div>
					<div className="relative flex justify-center">
						<span className="bg-card px-3 font-mono text-sm uppercase tracking-[0.14em] text-muted-foreground">
							Or
						</span>
					</div>
				</div>

				<Form {...form}>
					<form
						onSubmit={form.handleSubmit(onSubmit)}
						className="space-y-4"
						noValidate
					>
						<FormField
							control={form.control}
							name="email"
							render={({ field }) => (
								<FormItem>
									<FormLabel>Email</FormLabel>
									<div className="relative">
										<Mail className="absolute left-3 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
										<FormControl>
											<BrandInput
												autoFocus={autoFocus}
												type="email"
												autoComplete="email"
												placeholder="you@domain.com"
												className="pl-9"
												{...field}
											/>
										</FormControl>
									</div>
									<FormMessage />
								</FormItem>
							)}
						/>

						<FormField
							control={form.control}
							name="password"
							render={({ field }) => (
								<FormItem>
									<FormLabel>Password</FormLabel>
									<div className="relative">
										<Lock className="absolute left-3 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
										<FormControl>
											<BrandInput
												type="password"
												autoComplete="new-password"
												placeholder="min 8 chars"
												className="pl-9"
												{...field}
											/>
										</FormControl>
									</div>
									{field.value && (
										<div className="mt-2 flex items-center gap-3">
											<div className="flex flex-1 gap-1">
												{[1, 2, 3, 4, 5].map((level) => (
													<div
														key={level}
														className={`h-0.5 flex-1 transition-colors ${
															level <= passwordStrength.score
																? passwordStrength.color
																: "bg-muted"
														}`}
													/>
												))}
											</div>
											<span className="shrink-0 font-mono text-sm uppercase tracking-[0.12em] text-muted-foreground">
												{passwordStrength.label}
											</span>
										</div>
									)}
									<FormMessage />
								</FormItem>
							)}
						/>

						<FormField
							control={form.control}
							name="subscribeToRadar"
							render={({ field }) => (
								<FormItem>
									<label className="flex cursor-pointer items-start gap-3">
										<FormControl>
											<input
												type="checkbox"
												checked={field.value}
												onChange={field.onChange}
												onBlur={field.onBlur}
												name={field.name}
												ref={field.ref}
												className="mt-1 size-4 shrink-0 accent-brand"
											/>
										</FormControl>
										<span className="space-y-1">
											<span className="block text-base text-foreground">
												Agent market updates
											</span>
											<span className="block text-sm leading-relaxed text-muted-foreground">
												Follow new AI agents, breakout products, and verified
												momentum.
											</span>
										</span>
									</label>
									<FormMessage />
								</FormItem>
							)}
						/>

						<BrandButton
							type="submit"
							size="lg"
							disabled={loading}
							className="mt-1 w-full"
						>
							{loading ? (
								<>
									<Loader2 className="size-3.5 animate-spin" />
									Creating account
								</>
							) : (
								<>
									Create account
									<ArrowRight className="size-3.5" />
								</>
							)}
						</BrandButton>
					</form>
				</Form>
			</div>

			<p className="mt-5 text-center font-mono text-sm uppercase tracking-[0.12em] text-muted-foreground">
				By creating an account, you agree to our{" "}
				<Link
					to="/terms"
					className="text-foreground transition-colors hover:text-brand"
				>
					Terms
				</Link>{" "}
				and{" "}
				<Link
					to="/privacy"
					className="text-foreground transition-colors hover:text-brand"
				>
					Privacy
				</Link>
				.
			</p>
		</div>
	);
}
